import { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, addMonths, subMonths, parseISO, isSameDay } from "date-fns";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import MessagesPanel from "@/components/MessagesPanel";
import MessagesShell from "@/components/MessagesShell";
import { useAuth } from "@/lib/AuthContext";
import { Link } from "react-router-dom";
import DashboardFilters from "@/components/DashboardFilters";
import { CalendarDays, Clipboard, Siren, Repeat, Users, MessageSquare, User2, ChevronLeft, ChevronRight, Download, Plus, Check, X as XIcon, Inbox, LayoutDashboard, ArrowRightLeft, Send, UserCheck, Activity } from "lucide-react";

const NAV = [
  { to: "/anesthetist", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/anesthetist/schedule", label: "Schedule", icon: CalendarDays },
  { to: "/anesthetist/procedures", label: "Procedures", icon: Clipboard },
  { to: "/anesthetist/requests-inbox", label: "Requests", icon: Inbox },
  { to: "/anesthetist/emergency", label: "Emergency", icon: Siren },
  { to: "/anesthetist/requests", label: "Schedule Requests", icon: Repeat },
  { to: "/anesthetist/patients", label: "Patients", icon: Users },
  { to: "/anesthetist/messages", label: "Messages", icon: MessageSquare },
  { to: "/anesthetist/profile", label: "Profile", icon: User2 },
];

export default function AnesthetistDashboard() {
  return (
    <AppShell items={NAV}>
      <Routes>
        <Route index element={<DashboardTab />} />
        <Route path="schedule" element={<ScheduleTab />} />
        <Route path="procedures" element={<ProceduresTab />} />
        <Route path="requests-inbox" element={<ProcedureRequestsInbox />} />
        <Route path="emergency" element={<EmergencyTab />} />
        <Route path="requests" element={<RequestsTab />} />
        <Route path="patients" element={<PatientsTab />} />
        <Route path="messages" element={<MessagesShell><MessagesPanel contactsEndpoint="/anesthetist/contacts" /></MessagesShell>} />
        <Route path="profile" element={<ProfileTab />} />
        <Route path="*" element={<Navigate to="/anesthetist" replace />} />
      </Routes>
    </AppShell>
  );
}

// -------- Dashboard --------
function StatCard({ to, icon: Icon, label, value, sub, tone = "default" }) {
  const tones = {
    default: "from-white to-slate-50 text-slate-900 border-slate-200",
    teal: "from-[#339999]/10 to-white text-[#1E6B6B] border-[#339999]/30",
    amber: "from-amber-50 to-white text-amber-900 border-amber-200",
    red: "from-red-50 to-white text-red-900 border-red-200",
    green: "from-emerald-50 to-white text-emerald-900 border-emerald-200",
  };
  const inner = (
    <div className={`relative rounded-lg border bg-gradient-to-br ${tones[tone]} p-4 h-full transition-shadow hover:shadow-md`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className="text-3xl font-semibold mt-2" style={{ fontFamily: "Outfit" }}>{value ?? 0}</div>
      {sub && <div className="text-xs opacity-70 mt-1 line-clamp-2">{sub}</div>}
    </div>
  );
  if (to) return <Link to={to} className="block">{inner}</Link>;
  return inner;
}

function DashboardTab() {
  const { user } = useAuth();
  const [range, setRange] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!range) return;
    setLoading(true);
    api.get("/anesthetist/dashboard", { params: range })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    const iv = setInterval(() => {
      api.get("/anesthetist/dashboard", { params: range }).then((r) => setData(r.data)).catch(() => {});
    }, 15000);
    return () => clearInterval(iv);
  }, [range?.start_date, range?.end_date]);

  return (
    <div className="space-y-5" data-testid="anesthetist-dashboard">
      <div>
        <p className="aamg-label">Personal snapshot</p>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Welcome, {user?.name?.split(" ")[0] || "Doctor"}</h1>
        <p className="text-xs text-slate-500 mt-1 italic">Compassionate Care. Clinical Excellence.</p>
      </div>

      <Card className="aamg-card"><CardContent className="p-4">
        <DashboardFilters value={range} onChange={setRange} />
      </CardContent></Card>

      {loading && !data ? (
        <div className="text-sm text-slate-400 py-10 text-center">Loading snapshot…</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dashboard-stats">
            <StatCard tone={data.on_duty_today ? "green" : "default"} icon={UserCheck} label="On Duty Today"
              value={data.on_duty_today ? "Yes" : "No"} sub={data.today} />
            <StatCard to="/anesthetist" icon={CalendarDays} tone="teal" label="Procedures Today"
              value={data.procedures.today} sub={`${data.procedures.in_range} in range`} />
            <StatCard to="/anesthetist/requests-inbox" icon={Send} tone={data.surgeon_requests.pending_for_me > 0 ? "amber" : "default"} label="Surgeon Requests"
              value={data.surgeon_requests.pending_for_me} sub={`${data.surgeon_requests.accepted_in_range} accepted in range`} />
            <StatCard to="/anesthetist/emergency" icon={Siren} tone={data.emergency_cases.open > 0 ? "red" : "default"} label="Emergency Cases Open"
              value={data.emergency_cases.open} sub={`${data.emergency_cases.accepted_in_range} you took`} />
            <StatCard icon={ArrowRightLeft} tone={data.swaps.open_to_me > 0 ? "amber" : "default"} label="Open Swaps"
              value={data.swaps.open_to_me} sub={`${data.swaps.mine_pending} of yours pending`} />
            <StatCard icon={Repeat} label="Schedule Requests"
              value={data.schedule_requests.pending} sub="Awaiting admin" />
            <StatCard to="/anesthetist/messages" icon={MessageSquare} tone={data.messages.unread > 0 ? "amber" : "default"} label="Unread Messages"
              value={data.messages.unread} sub="Click to open" />
            <StatCard icon={Activity} label="Duty days in range"
              value={data.duty_days_in_range} sub="On-call shifts" />
          </div>

          <Card className="aamg-card">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#339999]" />Next 5 procedures</CardTitle></CardHeader>
            <CardContent>
              {data.procedures.upcoming.length === 0 ? (
                <div className="text-sm text-slate-400">No upcoming procedures</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.procedures.upcoming.map((p) => (
                    <li key={p.block_id} className="py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
                      <div>
                        <div className="font-medium text-slate-900">{p.date} · {p.start_time}–{p.end_time}</div>
                        <div className="text-xs text-slate-500">{p.patient_name || "—"} · {p.procedure_type || "—"}</div>
                      </div>
                      <Badge className="bg-[#339999]/10 text-[#339999] border-[#339999]/30">{p.location || "—"}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

function ScheduleTab() {
  const [month, setMonth] = useState(new Date());
  const [data, setData] = useState({ duty_dates: [], blocks: [] });
  const [dayDialog, setDayDialog] = useState(null); // date string yyyy-MM-dd or null
  const start = startOfMonth(month), end = endOfMonth(month);
  const days = useMemo(() => eachDayOfInterval({ start, end }), [start, end]);
  const startDate = format(start, "yyyy-MM-dd"), endDate = format(end, "yyyy-MM-dd");

  useEffect(() => {
    api.get(`/anesthetist/schedule`, { params: { start_date: startDate, end_date: endDate } }).then((r) => setData(r.data || { duty_dates: [], blocks: [] }));
  }, [startDate, endDate]);

  const blocksByDate = useMemo(() => {
    const map = {};
    (data.blocks || []).forEach((b) => { (map[b.date] = map[b.date] || []).push(b); });
    return map;
  }, [data]);

  const dayBlocks = dayDialog ? (blocksByDate[dayDialog] || []) : [];
  const dayIsOn = dayDialog ? (data.duty_dates || []).includes(dayDialog) : false;

  const downloadPdf = async () => {
    const res = await api.get(`/anesthetist/schedule/pdf`, { params: { start_date: startDate, end_date: endDate }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `schedule_${startDate}_${endDate}.pdf`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="anesth-schedule">
      <div className="flex items-center justify-between">
        <div>
          <p className="aamg-label">Calendar</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>My schedule</h1>
        </div>
        <Button variant="outline" onClick={downloadPdf} data-testid="schedule-pdf-btn"><Download className="h-4 w-4 mr-2" />PDF</Button>
      </div>
      <Card className="aamg-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={() => setMonth(subMonths(month, 1))} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>{format(month, "MMMM yyyy")}</h2>
            <Button variant="ghost" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 mb-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-2 py-1 text-center font-medium">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array(start.getDay()).fill(null).map((_, i) => <div key={`pad-${i}`} />)}
            {days.map((d) => {
              const ds = format(d, "yyyy-MM-dd");
              const isDuty = data.duty_dates?.includes(ds);
              const dayBlocks = blocksByDate[ds] || [];
              const clickable = isDuty || dayBlocks.length > 0;
              return (
                <button
                  type="button"
                  key={ds}
                  disabled={!clickable}
                  onClick={() => clickable && setDayDialog(ds)}
                  data-testid={`schedule-day-${ds}`}
                  className={`min-h-[88px] p-1.5 rounded-md border text-left transition ${isDuty ? "bg-[#339999]/5 border-[#339999]/30" : "bg-white border-slate-100"} ${clickable ? "hover:ring-2 hover:ring-[#339999]/40 cursor-pointer" : "cursor-default opacity-70"}`}
                >
                  <div className="flex justify-between items-center">
                    <span className={`text-xs font-medium ${isDuty ? "text-[#339999]" : "text-slate-600"}`}>{format(d, "d")}</span>
                    {isDuty && <span className="text-[9px] uppercase tracking-wider text-[#339999] font-bold">ON</span>}
                  </div>
                  <div className="space-y-0.5 mt-1">
                    {dayBlocks.slice(0, 3).map((b) => (
                      <div key={b.block_id} className="text-[10px] truncate bg-blue-50 text-blue-800 px-1.5 py-0.5 rounded" title={`${b.start_time}-${b.end_time} ${b.procedure_type}`}>
                        {b.start_time} {b.procedure_type || "Procedure"}
                      </div>
                    ))}
                    {dayBlocks.length > 3 && <div className="text-[10px] text-slate-400">+{dayBlocks.length - 3} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!dayDialog} onOpenChange={(o) => { if (!o) setDayDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="anesth-day-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>
              {dayDialog ? format(parseISO(dayDialog), "EEEE, MMMM d, yyyy") : ""}
            </DialogTitle>
            <div className="flex items-center gap-2 mt-1">
              {dayIsOn && <Badge className="bg-[#339999]/10 text-[#339999] border-[#339999]/30">On Duty</Badge>}
              <span className="text-xs text-slate-500">{dayBlocks.length} procedure{dayBlocks.length === 1 ? "" : "s"} assigned to you</span>
            </div>
          </DialogHeader>
          <div className="space-y-2 mt-2 max-h-[60vh] overflow-y-auto">
            {dayBlocks.length === 0 ? (
              <div className="text-sm text-slate-400 p-6 text-center">
                {dayIsOn ? "You're on duty but no procedures are assigned yet." : "No procedures assigned to you on this date."}
              </div>
            ) : dayBlocks.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")).map((b) => (
              <div key={b.block_id} data-testid={`day-block-${b.block_id}`} className="border border-slate-200 rounded-lg p-3 hover:border-[#339999]/50 transition">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 text-sm truncate">{b.procedure_type || "Procedure"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{b.patient_name || "—"}</div>
                  </div>
                  <div className="text-xs font-mono text-slate-700 whitespace-nowrap shrink-0">
                    {b.start_time || "?"}–{b.end_time || "?"}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                  {b.surgeon_name && <div><span className="text-slate-400">Surgeon:</span> <span className="text-slate-700">{b.surgeon_name}</span></div>}
                  {b.location && <div><span className="text-slate-400">Location:</span> <span className="text-slate-700">{b.location}</span></div>}
                </div>
                {b.notes && <div className="text-xs text-slate-600 mt-2 italic bg-slate-50 rounded p-2">{b.notes}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProceduresTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/anesthetist/procedures").then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="space-y-5" data-testid="anesth-procedures">
      <div><p className="aamg-label">List</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Procedures</h1></div>
      <Card className="aamg-card">
        <CardContent className="p-0 overflow-x-auto">
          {items.length === 0 ? <div className="p-10 text-center text-slate-400">No procedures assigned</div> : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Time</th><th className="text-left px-4 py-2">Patient</th><th className="text-left px-4 py-2">Procedure</th><th className="text-left px-4 py-2">Surgeon</th><th className="text-left px-4 py-2">Location</th></tr>
              </thead>
              <tbody>{items.map((b) => (
                <tr key={b.block_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-2 font-medium">{b.date}</td>
                  <td className="px-4 py-2">{b.start_time}–{b.end_time}</td>
                  <td className="px-4 py-2">{b.patient_name || "—"}</td>
                  <td className="px-4 py-2">{b.procedure_type || "—"}</td>
                  <td className="px-4 py-2">{b.surgeon_name || "—"}</td>
                  <td className="px-4 py-2">{b.location || "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EmergencyTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ patient_name: "", surgeon_name: "", procedure: "", diagnosis: "", location: "", date_of_service: "", time_started: "", time_ended: "", notes: "" });
  const { user } = useAuth();
  const refresh = () => api.get("/anesthetist/emergency-cases").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);
  const create = async () => {
    if (!form.patient_name || !form.date_of_service) { toast.error("Patient + date required"); return; }
    await api.post("/anesthetist/emergency-cases", form); toast.success("Emergency case posted"); setOpen(false);
    setForm({ patient_name: "", surgeon_name: "", procedure: "", diagnosis: "", location: "", date_of_service: "", time_started: "", time_ended: "", notes: "" });
    refresh();
  };
  const accept = async (id) => {
    try { await api.post(`/anesthetist/emergency-cases/${id}/accept`); toast.success("Case accepted"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
  };
  const cancel = async (id) => { await api.delete(`/anesthetist/emergency-cases/${id}`); refresh(); };

  return (
    <div className="space-y-5" data-testid="anesth-emergency">
      <div className="flex items-center justify-between">
        <div><p className="aamg-label">Marketplace</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Emergency cases</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="emergency-post-btn"><Plus className="h-4 w-4 mr-2" />Post case</Button></DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Post an emergency case</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Patient name"><Input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} data-testid="em-patient" /></Field>
              <Field label="Surgeon"><Input value={form.surgeon_name} onChange={(e) => setForm({ ...form, surgeon_name: e.target.value })} /></Field>
              <Field label="Procedure"><Input value={form.procedure} onChange={(e) => setForm({ ...form, procedure: e.target.value })} /></Field>
              <Field label="Diagnosis"><Input value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} /></Field>
              <Field label="Location" colSpan={2}><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
              <Field label="Date"><Input type="date" value={form.date_of_service} onChange={(e) => setForm({ ...form, date_of_service: e.target.value })} data-testid="em-date" /></Field>
              <Field label="Time started"><Input type="time" value={form.time_started} onChange={(e) => setForm({ ...form, time_started: e.target.value })} /></Field>
              <Field label="Time ended"><Input type="time" value={form.time_ended} onChange={(e) => setForm({ ...form, time_ended: e.target.value })} /></Field>
              <Field label="Notes" colSpan={2}><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
            <Button onClick={create} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="em-submit-btn">Post case</Button>
          </DialogContent>
        </Dialog>
      </div>
      <Card className="aamg-card">
        <CardContent className="p-0 overflow-x-auto">
          {items.length === 0 ? <div className="p-10 text-center text-slate-400">No emergency cases</div> : (
            <ul className="divide-y divide-slate-100">
              {items.map((c) => (
                <li key={c.case_id} className="p-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900">{c.patient_name}</span>
                      <Badge className={`border text-[10px] capitalize ${c.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : c.status === "accepted" ? "bg-green-50 text-green-700 border-green-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{c.status}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{c.date_of_service} {c.time_started}–{c.time_ended} · {c.procedure || "—"} · {c.location || "—"}</div>
                    <div className="text-xs text-slate-400 mt-0.5">Requester: {c.requester_name} {c.accepted_by_name && `· Accepted by ${c.accepted_by_name}`}</div>
                  </div>
                  <div className="flex gap-2">
                    {c.status === "pending" && c.requester_id !== user?.user_id && <Button size="sm" onClick={() => accept(c.case_id)} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid={`em-accept-${c.case_id}`}>Accept</Button>}
                    {c.status === "pending" && c.requester_id === user?.user_id && <Button size="sm" variant="outline" onClick={() => cancel(c.case_id)}><XIcon className="h-3 w-3 mr-1" />Cancel</Button>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RequestsTab() {
  const { user } = useAuth();
  const [swaps, setSwaps] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [form, setForm] = useState({ date: "", target_anesthetist_id: "any", reason: "" });
  const refresh = () => api.get("/anesthetist/swap-requests").then((r) => setSwaps(r.data || []));
  useEffect(() => {
    refresh();
    api.get("/anesthetist/colleagues").then((r) => setColleagues(r.data || []));
    const iv = setInterval(refresh, 5000);
    return () => clearInterval(iv);
  }, []);
  const submit = async () => {
    if (!form.date) { toast.error("Date required"); return; }
    const target = form.target_anesthetist_id === "any" ? "" : form.target_anesthetist_id;
    try {
      await api.post("/anesthetist/swap-requests", { date: form.date, target_anesthetist_id: target, reason: form.reason });
      toast.success("Swap request sent — colleagues notified");
      setForm({ date: "", target_anesthetist_id: "any", reason: "" });
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const accept = async (sid) => {
    try { await api.post(`/anesthetist/swap-requests/${sid}/accept`); toast.success("Swap accepted — duty roster updated"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
  };
  const cancel = async (sid) => { await api.delete(`/anesthetist/swap-requests/${sid}`); refresh(); };

  return (
    <div className="space-y-5" data-testid="anesth-swap-requests">
      <div><p className="aamg-label">Shift swaps</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Swap requests</h1>
        <p className="text-slate-500 text-sm mt-1">Direct swap — no admin approval needed. The accepting anesthetist takes the duty automatically.</p>
      </div>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Request a swap</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="swap-date" /></Field>
          <Field label="Swap with">
            <Select value={form.target_anesthetist_id} onValueChange={(v) => setForm({ ...form, target_anesthetist_id: v })}>
              <SelectTrigger data-testid="swap-target"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any colleague (broadcast)</SelectItem>
                {colleagues.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reason" colSpan={2}><Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Personal reasons / appointment / etc." /></Field>
          <div className="col-span-2"><Button onClick={submit} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="swap-submit-btn">Send swap request</Button></div>
        </CardContent>
      </Card>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Open swaps</CardTitle></CardHeader>
        <CardContent>
          {swaps.length === 0 ? <div className="text-sm text-slate-400">No swap activity</div> : (
            <ul className="divide-y divide-slate-100">{swaps.map((s) => {
              const mine = s.requester_id === user?.user_id;
              const canAccept = !mine && s.status === "pending" && (!s.target_anesthetist_id || s.target_anesthetist_id === user?.user_id);
              return (
                <li key={s.swap_id} className="py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900">{mine ? "You" : s.requester_name}</span>
                      <span className="text-xs text-slate-500">wants to swap</span>
                      <span className="font-semibold text-[#339999]">{s.date}</span>
                      {!s.target_anesthetist_id && s.status === "pending" && <Badge className="border bg-blue-50 text-blue-700 border-blue-200 text-[10px]">Open to all</Badge>}
                      <Badge className={`border capitalize text-[10px] ${s.status === "accepted" ? "bg-green-50 text-green-700 border-green-200" : s.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{s.status}</Badge>
                    </div>
                    {s.reason && <div className="text-xs text-slate-500 mt-1 italic">"{s.reason}"</div>}
                    {s.accepted_by_name && <div className="text-xs text-green-700 mt-0.5 flex items-center gap-1"><Check className="h-3 w-3" /> Accepted by {s.accepted_by_name}</div>}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {canAccept && <Button size="sm" onClick={() => accept(s.swap_id)} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid={`swap-accept-${s.swap_id}`}>Accept</Button>}
                    {mine && s.status === "pending" && <Button size="sm" variant="outline" className="text-red-600" onClick={() => cancel(s.swap_id)}><XIcon className="h-3 w-3 mr-1" />Cancel</Button>}
                  </div>
                </li>
              );
            })}</ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PatientsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/anesthetist/patients").then((r) => setItems(r.data || [])); }, []);
  return (
    <div className="space-y-5" data-testid="anesth-patients">
      <div><p className="aamg-label">Assigned</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Patients</h1></div>
      <Card className="aamg-card">
        <CardContent className="p-0 overflow-x-auto">
          {items.length === 0 ? <div className="p-10 text-center text-slate-400">No assigned patients</div> : (
            <ul className="divide-y divide-slate-100">
              {items.map((b, i) => (
                <li key={i} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-slate-900">{b.patient_name || "—"}</div>
                    <div className="text-xs text-slate-500">{b.date} · {b.procedure_type || "—"} · {b.surgeon_name}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProfileTab() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", bio: user?.bio || "" });
  const [pw, setPw] = useState("");
  const [calendar, setCalendar] = useState({ configured: false, connected: false, connected_at: null });

  const loadCalendar = async () => {
    try { const { data } = await api.get("/calendar/status"); setCalendar(data); } catch {}
  };

  useEffect(() => {
    loadCalendar();
    const params = new URLSearchParams(window.location.search);
    if (params.get("calendar") === "connected") { toast.success("Google Calendar connected"); window.history.replaceState(null, "", window.location.pathname); }
    if (params.get("calendar") === "error") { toast.error("Calendar connection failed"); window.history.replaceState(null, "", window.location.pathname); }
  }, []);

  const save = async () => { await api.put("/anesthetist/profile", form); toast.success("Profile updated"); refresh(); };
  const changePw = async () => {
    if (pw.length < 6) { toast.error("Min 6 chars"); return; }
    await api.put("/anesthetist/password", { new_password: pw }); toast.success("Password updated"); setPw("");
  };
  const connectCal = async () => {
    try {
      const { data } = await api.get("/calendar/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Calendar not configured on server");
    }
  };
  const disconnectCal = async () => { await api.post("/calendar/disconnect"); toast.success("Disconnected"); loadCalendar(); };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="anesth-profile">
      <div><p className="aamg-label">Account</p><h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Profile</h1></div>
      <Card className="aamg-card"><CardHeader><CardTitle className="text-lg">Personal information</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          <Field label="Full name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Bio" colSpan={2}><Textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} /></Field>
          <div className="col-span-2"><Button onClick={save} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="profile-save-btn">Save changes</Button></div>
        </CardContent>
      </Card>
      <Card className="aamg-card"><CardHeader><CardTitle className="text-lg">Change password</CardTitle></CardHeader>
        <CardContent className="flex gap-2 items-end">
          <Field label="New password"><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
          <Button onClick={changePw} className="bg-[#339999] hover:bg-[#2A7F7F]">Update</Button>
        </CardContent>
      </Card>
      <Card className="aamg-card">
        <CardHeader><CardTitle className="text-lg">Google Calendar sync</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!calendar.configured ? (
            <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3">
              Google Calendar is not configured on the server yet. Once an administrator sets <code className="font-mono text-xs">GOOGLE_CLIENT_ID</code> and <code className="font-mono text-xs">GOOGLE_CLIENT_SECRET</code>, you'll be able to connect.
            </div>
          ) : calendar.connected ? (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-md px-4 py-3">
              <div>
                <div className="text-sm font-medium text-green-900">Connected to Google Calendar</div>
                <div className="text-xs text-green-700 mt-0.5">Procedure blocks assigned to you will be pushed automatically.{calendar.connected_at && ` · Linked ${calendar.connected_at.slice(0, 10)}`}</div>
              </div>
              <Button variant="outline" size="sm" onClick={disconnectCal} data-testid="cal-disconnect-btn">Disconnect</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Connect your Google Calendar so your AAMG procedure blocks appear alongside your personal schedule.</p>
              <Button onClick={connectCal} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid="cal-connect-btn">
                <svg className="h-4 w-4 mr-2" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.6 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.3 35 26.8 36 24 36c-5.3 0-9.8-3.4-11.3-8L6 28c3.3 6.6 10 10 18 10z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.6 5l6.3 5.3C42 35 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
                Connect Google Calendar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children, colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="field-label">{label}</Label>
      {children}
    </div>
  );
}

function ProcedureRequestsInbox() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const refresh = () => api.get("/anesthetist/procedure-requests").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); const iv = setInterval(refresh, 15000); return () => clearInterval(iv); }, []);

  const accept = async (rid) => {
    try { await api.post(`/anesthetist/procedure-requests/${rid}/accept`); toast.success("Request accepted — procedure block created"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
  };
  const decline = async (rid) => {
    const reason = prompt("Optional reason for declining:") || "";
    try { await api.post(`/anesthetist/procedure-requests/${rid}/decline`, { decline_reason: reason }); toast.success("Declined"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not decline"); }
  };

  const filtered = filter === "all" ? items : items.filter((r) => r.status === filter);

  return (
    <div className="space-y-5" data-testid="anesth-procedure-requests">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="aamg-label">Inbox</p>
          <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit" }}>Procedure requests</h1>
          <p className="text-slate-500 text-sm mt-1">Requests from surgeons addressed to you or open to any anesthetist.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="req-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="accepted">Accepted</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Card className="aamg-card">
        <CardContent className="p-0 overflow-x-auto">
          {filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No {filter === "all" ? "" : filter} requests.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {filtered.map((r) => {
                const isOpenToAny = !r.target_anesthetist_id;
                const acceptedByMe = r.accepted_by_id === user?.user_id;
                return (
                  <li key={r.request_id} className="p-4" data-testid={`incoming-${r.request_id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">{r.procedure_type}</span>
                          <Badge className={`border capitalize text-[10px] ${r.status === "pending" ? "bg-amber-50 text-amber-800 border-amber-200" : r.status === "accepted" ? "bg-green-50 text-green-700 border-green-200" : r.status === "declined" ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.status}</Badge>
                          {isOpenToAny && r.status === "pending" && <Badge className="border bg-blue-50 text-blue-700 border-blue-200 text-[10px]">Open to all</Badge>}
                          {acceptedByMe && <Badge className="border bg-[#339999]/10 text-[#339999] border-[#339999]/30 text-[10px]">You</Badge>}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          From <strong className="text-slate-700">Dr. {r.surgeon_name}</strong> · Patient {r.patient_name}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {r.date} {r.start_time}{r.end_time && `–${r.end_time}`} · {r.location || "—"}
                        </div>
                        {r.notes && <div className="text-xs text-slate-600 mt-2 italic bg-slate-50 border border-slate-100 rounded px-2 py-1.5">"{r.notes}"</div>}
                      </div>
                      {r.status === "pending" && (
                        <div className="flex gap-2 shrink-0">
                          <Button size="sm" onClick={() => accept(r.request_id)} className="bg-[#339999] hover:bg-[#2A7F7F]" data-testid={`accept-req-${r.request_id}`}>
                            <Check className="h-3 w-3 mr-1" /> Accept
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decline(r.request_id)} className="text-red-600" data-testid={`decline-req-${r.request_id}`}>
                            <XIcon className="h-3 w-3 mr-1" /> Decline
                          </Button>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
